// hooks.ts - lightweight data-fetching hooks built on useState/useEffect only.
// (react-query / react-router are intentionally banned in this project - AGENTS.md.)
//
// 도메인 리소스 훅(useDashboard / useDevItem / useIssues …)은 이 파일 하단에서
// `useResource` / `useCrudResource` 위에 얹혀 있다. fetch 패턴은 하나뿐이다.
import {
  type CompanySettings,
  type Customer,
  type DashboardFilters,
  type DashboardItem,
  type DashboardSort,
  type DashboardSummary,
  type DevItem,
  type DevItemDetail,
  type Holiday,
  type IssueFilters,
  type IssueRow,
  type LeadTimeTemplate,
  type MilestoneLabel,
  type MyTasks,
  type ProcessMaster,
  type Staff,
  type AdminUser,
  type CalendarEvent,
  customersApi,
  getCalendar,
  getErpMeta,
  getErpOptions,
  runErpView,
  getDashboardItems,
  getDashboardSummary,
  getDevItem,
  getMilestoneLabels,
  getMyTasks,
  getSettings,
  holidaysApi,
  leadTimeApi,
  listDevItems,
  listIssues,
  listUsers,
  processesApi,
  staffApi,
} from "@/lib/api";
import type { ErpMeta, ErpOption, ErpViewResult } from "@/lib/erp";
import { type RibbonCell, buildRibbon, ribbonEnd } from "@/lib/ribbon";
import { useCallback, useEffect, useMemo, useState } from "react";

export interface Resource<T> {
  data: T;
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
}

const asError = (e: unknown): Error => (e instanceof Error ? e : new Error(String(e)));

// Generic async resource. `fetcher` must be stable (module-level fn or wrapped in
// useCallback by the caller) so the effect re-runs only when its inputs change.
export function useResource<T>(fetcher: () => Promise<T>, initial: T): Resource<T> {
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetcher());
    } catch (e) {
      setError(asError(e));
    } finally {
      setLoading(false);
    }
  }, [fetcher]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fetcher()
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(asError(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [fetcher]);

  return { data, loading, error, reload };
}

export interface CrudResource<T> extends Resource<T[]> {
  create: (item: Omit<T, "id"> & { id?: string }) => Promise<T>;
  update: (id: string, patch: Partial<T>) => Promise<T>;
  remove: (id: string) => Promise<void>;
}

export interface CrudApi<T> {
  list: () => Promise<T[]>;
  create: (item: Omit<T, "id"> & { id?: string }) => Promise<T>;
  update: (id: string, patch: Partial<T>) => Promise<T>;
  remove: (id: string) => Promise<void>;
}

// Wrap a mutation so it runs, triggers a reload of the list, then resolves with
// the server's result.
async function withReload<R>(op: Promise<R>, reload: () => Promise<void>): Promise<R> {
  const result = await op;
  await reload();
  return result;
}

// 표준 목록+CRUD 리소스. `sort` 를 주면 서버 문서 순서 대신 그 비교자로 정렬한다.
export function useCrudResource<T>(api: CrudApi<T>, sort?: (items: T[]) => T[]): CrudResource<T> {
  const res = useResource(api.list, [] as T[]);
  // useMemo 로 배열 identity 를 고정해야 소비처 useMemo 의존성이 매 렌더 깨지지 않는다.
  const data = useMemo(() => (sort ? sort(res.data) : res.data), [res.data, sort]);
  return {
    ...res,
    data,
    create: (item) => withReload(api.create(item), res.reload),
    update: (id, patch) => withReload(api.update(id, patch), res.reload),
    remove: (id) => withReload(api.remove(id), res.reload),
  };
}

/* =====================================================================
   도메인 훅 (§05 §13-2)

   전부 위의 `useResource` / `useCrudResource` 위에 얹는다. react-query 를
   쓰지 않으므로 캐시가 없고, 화면 진입 시 항상 새로 읽는다 (REQ-FLOW-006).
   fetcher 는 `useCallback` 으로 고정해야 effect 가 매 렌더 다시 돌지 않는다.
   필터 객체는 렌더마다 새로 만들어지므로 직렬화한 키를 의존성으로 쓴다.
   ===================================================================== */

const EMPTY_LIST: never[] = [];

/**
 * Dashboard - `/dashboard/summary` + `/dashboard/items` **2개**만 부른다
 * (REQ-GOAL-011 AC2). 두 리소스를 따로 두는 이유: §04 가 "KPI·표 영역 각각
 * 독립" 오류 상태를 요구한다. 한쪽이 실패해도 다른 쪽은 보인다.
 */
export function useDashboard(
  filters: DashboardFilters,
  sort: DashboardSort = "default",
  order: "asc" | "desc" = "asc"
): {
  summary: Resource<DashboardSummary | null>;
  items: Resource<{ total: number; items: DashboardItem[] }>;
} {
  const key = JSON.stringify([filters, sort, order]);
  const fetchSummary = useCallback(() => getDashboardSummary(filters), [key]);
  const fetchItems = useCallback(() => getDashboardItems(filters, sort, order), [key]);
  return {
    summary: useResource<DashboardSummary | null>(fetchSummary, null),
    items: useResource(fetchItems, { total: 0, items: [] as DashboardItem[] }),
  };
}

/**
 * 주차 리본 - 다가오는 N주의 일정 (§4-1 시간축).
 *
 * 대시보드의 **세 번째** 호출이다. §8-1 은 오랫동안 "도메인 조회 2개"를
 * 유지해 왔고 그 숫자는 §20 "10초 파악" 의 근거 중 하나였다. 하나를 더
 * 붙이는 대신 두 가지를 지킨다.
 *   1. 병렬이다 - summary/items 를 기다리지 않는다.
 *   2. 표를 막지 않는다 - 리본은 자기 자리에서만 로딩하고,
 *      느리거나 실패해도 §4-2 표는 그대로 뜬다.
 * 즉 늘어난 것은 호출 수지 첫 화면까지의 시간이 아니다.
 */
export function useRibbon(asOf: string, weeks: number): Resource<RibbonCell[]> {
  const fetcher = useCallback(async () => {
    if (!asOf) return [];
    const events = await getCalendar(asOf, ribbonEnd(asOf, weeks), { includeProduction: true });
    return buildRibbon(events, asOf, weeks);
  }, [asOf, weeks]);
  return useResource<RibbonCell[]>(fetcher, []);
}

/** 상세화면 - `GET /dev-items/{id}` 하나가 7블록 전부를 준다 (§05 A-2). */
export function useDevItem(id: string | null): Resource<DevItemDetail | null> {
  const fetcher = useCallback(() => (id ? getDevItem(id) : Promise.resolve(null)), [id]);
  return useResource<DevItemDetail | null>(fetcher, null);
}

/** 셀렉트·자동완성용 기본 목록 (파생값 없음). */
export function useDevItems(
  filters: { customerId?: string; staffId?: string; q?: string; includeArchived?: boolean } = {}
): Resource<DevItem[]> {
  const key = JSON.stringify(filters);
  const fetcher = useCallback(() => listDevItems(filters), [key]);
  return useResource<DevItem[]>(fetcher, EMPTY_LIST);
}

/** §11 지연·이슈. `red` 인 항목을 계산으로 산출한 뒤 issues 를 left-join 한 행들. */
export function useIssues(filters: IssueFilters = {}): Resource<IssueRow[]> {
  const key = JSON.stringify(filters);
  const fetcher = useCallback(() => listIssues(filters), [key]);
  return useResource<IssueRow[]>(fetcher, EMPTY_LIST);
}

/** §13 Calendar. 범위는 화면의 그리드가 실제로 덮는 날짜다. */
export function useCalendar(
  from: string,
  to: string,
  filters: { customerId?: string; staffId?: string; includeProduction?: boolean } = {}
): Resource<CalendarEvent[]> {
  const key = JSON.stringify([from, to, filters]);
  const fetcher = useCallback(() => getCalendar(from, to, filters), [key]);
  return useResource<CalendarEvent[]>(fetcher, EMPTY_LIST);
}

/** §14 내 업무. `staffId` 는 admin 만 남의 것을 지정할 수 있다 (REQ-MY-007). */
export function useMyTasks(staffId?: string): Resource<MyTasks | null> {
  const fetcher = useCallback(() => getMyTasks(staffId), [staffId]);
  return useResource<MyTasks | null>(fetcher, null);
}

/* ── 마스터 ─────────────────────────────────────────────────────── */

export const useStaff = (): CrudResource<Staff> => useCrudResource(staffApi);
export const useCustomers = (): CrudResource<Customer> => useCrudResource(customersApi);
export const useProcesses = (): CrudResource<ProcessMaster> => useCrudResource(processesApi);
export const useLeadTimeTemplates = (): CrudResource<LeadTimeTemplate> =>
  useCrudResource(leadTimeApi);

/** 공휴일은 수정이 없다(날짜가 곧 id). 그래서 CrudResource 대신 얇은 훅. */
export function useHolidays(year?: number): Resource<Holiday[]> & {
  add: (date: string, name: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
} {
  const fetcher = useCallback(() => holidaysApi.list(year), [year]);
  const res = useResource<Holiday[]>(fetcher, EMPTY_LIST);
  return {
    ...res,
    add: async (date, name) => {
      await holidaysApi.create({ date, name });
      await res.reload();
    },
    remove: async (id) => {
      await holidaysApi.remove(id);
      await res.reload();
    },
  };
}

export const useSettings = (): Resource<CompanySettings | null> =>
  useResource<CompanySettings | null>(getSettings, null);

export const useMilestoneLabels = (): Resource<MilestoneLabel[]> =>
  useResource<MilestoneLabel[]>(getMilestoneLabels, EMPTY_LIST);

export const useUsers = (): Resource<AdminUser[]> =>
  useResource<AdminUser[]>(listUsers, EMPTY_LIST);

/* =====================================================================
   ERP 업무 (기존 ERP 이관)
   ===================================================================== */

/** 모듈·뷰·엔티티 명세. 셸 수명 동안 바뀌지 않는다. */
export const useErpMeta = (): Resource<ErpMeta | null> =>
  useResource<ErpMeta | null>(getErpMeta, null);

/**
 * 뷰 실행 결과. `paramsKey` 는 검색조건의 직렬화 문자열이다 - 객체 identity 가
 * 렌더마다 바뀌어도 조건이 같으면 다시 읽지 않는다.
 */
export function useErpView(
  key: string | null,
  params: Record<string, string>,
  page: number,
  pageSize: number
): Resource<ErpViewResult | null> {
  const paramsKey = JSON.stringify(params);
  // 의존성은 params 대신 paramsKey - 값이 같으면 같은 fetcher (불필요한 재조회 방지)
  const fetcher = useCallback(
    async () => (key ? runErpView(key, params, (page - 1) * pageSize, pageSize) : null),
    [key, paramsKey, page, pageSize]
  );
  return useResource<ErpViewResult | null>(fetcher, null);
}

// 참조 선택지 캐시 - 같은 엔티티(제품 680건 등)를 필터와 폼이 각각 다시 읽지 않게.
// 쓰기 후에는 `invalidateErpOptions` 로 비운다.
const optionCache = new Map<string, Promise<ErpOption[]>>();

export function invalidateErpOptions(entity?: string): void {
  if (entity) optionCache.delete(entity);
  else optionCache.clear();
}

export function useErpOptions(entity: string | null): ErpOption[] {
  const [opts, setOpts] = useState<ErpOption[]>(EMPTY_LIST as ErpOption[]);
  useEffect(() => {
    if (!entity) return;
    let alive = true;
    let p = optionCache.get(entity);
    if (!p) {
      p = getErpOptions(entity).catch((e) => {
        optionCache.delete(entity);
        throw e;
      });
      optionCache.set(entity, p);
    }
    p.then((o) => alive && setOpts(o)).catch(() => alive && setOpts([]));
    return () => {
      alive = false;
    };
  }, [entity]);
  return opts;
}
